import { TFunction } from 'i18next'

import { CONFIG_ENABLE_EDD } from '@src/config/env'
import keyBy from '@src/quince-site-common/src/utils/keyBy'

import { EDDDetails, EDDProductType, VariantDetail } from '../../../services/eddService/eddService.types'

export const getEDDDetails = (
  variantEDDDetails: number[] | undefined,
  eddDetails: EDDProductType | undefined,
  zipCode?: string | null,
): EDDDetails => {
  let isServiceable = true

  if (!CONFIG_ENABLE_EDD || !zipCode) {
    return {
      showEDD: false,
      isExpressShipping: false,
      isServiceable,
    }
  }

  let standardDeliveryEDD: EDDDetails | null = null
  let standardPremiumDeliveryEDD: EDDDetails | null = null
  const allDisplayTags: string[] = []

  variantEDDDetails?.forEach(variantEDDDetail => {
    const eddDetail: VariantDetail | undefined = eddDetails?.[variantEDDDetail]
    const inventoryLocations = eddDetail?.location_ids
    // for true or key not present consider as true only when keys is present and false it is false case
    isServiceable = eddDetail?.is_serviceable !== false

    if (eddDetail) {
      const eddDetailMap = keyBy(eddDetail.delivery_types, `type`)
      const delivery = eddDetailMap.LB_STANDARD
      if (delivery?.is_faster_edd) {
        standardDeliveryEDD = {
          ...delivery,
          inventoryLocations,
          showEDD: delivery.is_faster_edd,
          isExpressShipping: false,
          cutoffDate: delivery.cutoff_epoch,
          cutoffTTL: delivery.cutoff_ttl_sec,
          isServiceable,
          displayTags: delivery?.display_tags,
        }
      }
      // Extract display_tags even if is_faster_edd is false
      // Merge all tags from all variants (only non-empty arrays)
      if (delivery?.display_tags && Array.isArray(delivery.display_tags) && delivery.display_tags.length > 0) {
        allDisplayTags.push(...delivery.display_tags)
      }

      const standardPremiumDelivery = eddDetailMap.LB_STANDARD_PREMIUM
      if (standardPremiumDelivery?.is_faster_edd) {
        standardPremiumDeliveryEDD = {
          ...standardPremiumDelivery,
          inventoryLocations,
          showEDD: standardPremiumDelivery.is_faster_edd,
          isExpressShipping: false,
          cutoffDate: standardPremiumDelivery.cutoff_epoch,
          cutoffTTL: standardPremiumDelivery.cutoff_ttl_sec,
          isServiceable,
          displayTags: standardPremiumDelivery.display_tags,
        }
      }
      // Extract display_tags from premium delivery even if is_faster_edd is false
      // Merge all tags from all variants (only non-empty arrays)
      if (
        standardPremiumDelivery?.display_tags &&
        Array.isArray(standardPremiumDelivery.display_tags) &&
        standardPremiumDelivery.display_tags.length > 0
      ) {
        allDisplayTags.push(...standardPremiumDelivery.display_tags)
      }
    }
  })

  // Remove duplicates and convert to undefined if empty
  const displayTagsFromDelivery = allDisplayTags.length > 0 ? Array.from(new Set(allDisplayTags)) : undefined

  if (standardPremiumDeliveryEDD) return standardPremiumDeliveryEDD

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (standardDeliveryEDD) return standardDeliveryEDD

  const firstVariantId =
    variantEDDDetails != null && eddDetails != null && variantEDDDetails.length ? variantEDDDetails[0] : undefined
  const eddDetail = firstVariantId !== undefined && eddDetails ? eddDetails[String(firstVariantId)] : undefined
  const delivery = eddDetail && keyBy(eddDetail.delivery_types, 'type').LB_STANDARD
  const eddDetailsData: EDDDetails | undefined = delivery && {
    ...delivery,
    showEDD: delivery.is_faster_edd,
    isExpressShipping: false,
    inventoryLocations: eddDetail.location_ids,
    type: delivery.type,
    edd_type: delivery.edd_type,
    max_days: delivery.max_cal_days,
    min_days: delivery.min_cal_days,
  }

  // Return default with display_tags if present (even when is_faster_edd is false)
  return {
    showEDD: false,
    isExpressShipping: false,
    isServiceable,
    displayTags: displayTagsFromDelivery,
    ...(eddDetailsData ?? {}),
  }
}

export const useEDDDetailFromSelectedOptions = (
  eddDetails: EDDProductType | undefined,
  selectedOptionsVariantIds: number[] | undefined,
  zipCode?: string,
) => {
  return getEDDDetails(selectedOptionsVariantIds, eddDetails, zipCode)
}

export const generateEDDLabel = (eddDetails: EDDDetails | undefined, t: TFunction) => {
  if (!eddDetails?.max_date || !eddDetails.showEDD) return

  let dateInDateStringFormat = eddDetails.max_date

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

  if (dateRegex.test(dateInDateStringFormat)) {
    dateInDateStringFormat += `T00:00:00`
  }

  const date = new Date(dateInDateStringFormat)

  const day = date.getDay()
  const dayOfMonth = date.getDate()

  // Month names for formatted display
  const MONTH_NAMES = [
    t('jan'),
    t('feb'),
    t('mar'),
    t('apr'),
    t('may'),
    t('jun'),
    t('jul'),
    t('aug'),
    t('sep'),
    t('oct'),
    t('nov'),
    t('dec'),
  ]

  const DAY_NAMES = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')]

  let label = t('free_delivery_by_var1_var2_var3', {
    var1: DAY_NAMES[day],
    var2: MONTH_NAMES[date.getMonth()],
    var3: dayOfMonth,
  })

  if (eddDetails.type === 'LB_STANDARD_PREMIUM') {
    label = t('estimated_delivery_with_days', {
      day: DAY_NAMES[day],
      month: MONTH_NAMES[date.getMonth()],
      dayOfMonth: dayOfMonth,
      days: eddDetails.max_cal_days,
    })
  } else if (eddDetails.max_cal_days === 0) {
    label = t('free_delivery_today_template', { month: MONTH_NAMES[date.getMonth()], dayOfMonth: dayOfMonth })
  } else if (eddDetails.max_cal_days === 1) {
    label = t('free_delivery_tomorrow_template', { var1: MONTH_NAMES[date.getMonth()], dayOfMonth: dayOfMonth })
  } else if (eddDetails.max_cal_days === 2) {
    label = t('free_2_day_delivery_by_va_l8g8oi', {
      var1: DAY_NAMES[day],
      var2: MONTH_NAMES[date.getMonth()],
      dayOfMonth: dayOfMonth,
    })
  }

  return label
}

/**
 * Generates EDD label for bottom tag component with simplified labels (no date/day) for today, tomorrow, and 2-day delivery
 * @param eddDetails - EDD details object
 * @param t - Translation function
 * @returns EDD label string or undefined
 */
export const generateEDDLabelForBottomTag = (eddDetails: EDDDetails | undefined, t: TFunction) => {
  if (!eddDetails?.max_date || !eddDetails.showEDD) return

  let dateInDateStringFormat = eddDetails.max_date

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

  if (dateRegex.test(dateInDateStringFormat)) {
    dateInDateStringFormat += `T00:00:00`
  }

  const date = new Date(dateInDateStringFormat)

  const day = date.getDay()
  const dayOfMonth = date.getDate()

  // Month names for formatted display
  const MONTH_NAMES = [
    t('jan'),
    t('feb'),
    t('mar'),
    t('apr'),
    t('may'),
    t('jun'),
    t('jul'),
    t('aug'),
    t('sep'),
    t('oct'),
    t('nov'),
    t('dec'),
  ]

  const DAY_NAMES = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')]

  let label = t('free_delivery_by_var1_var2_var3', {
    var1: DAY_NAMES[day],
    var2: MONTH_NAMES[date.getMonth()],
    var3: dayOfMonth,
  })

  if (eddDetails.type === 'LB_STANDARD_PREMIUM') {
    label = t('estimated_delivery_with_days', {
      day: DAY_NAMES[day],
      month: MONTH_NAMES[date.getMonth()],
      dayOfMonth: dayOfMonth,
      days: eddDetails.max_cal_days,
    })
  } else if (eddDetails.max_cal_days === 0) {
    // Simplified label without date: "FREE Delivery Today"
    label = t('section_renderer.free_delivery_today')
  } else if (eddDetails.max_cal_days === 1) {
    // Simplified label without date: "FREE Delivery Tomorrow"
    label = t('section_renderer.free_delivery_tomorrow')
  } else if (eddDetails.max_cal_days === 2) {
    // Simplified label without date: "FREE 2-Day Delivery"
    label = t('section_renderer.free_2_day_delivery')
  }

  return label
}

export const getCutoffTime = (cutoffDateInEpochSeconds: number, t: TFunction) => {
  const currentDateInEpochSeconds = Math.floor(Date.now() / 1000)
  const totalSeconds = cutoffDateInEpochSeconds - currentDateInEpochSeconds

  if (totalSeconds <= 0) {
    return null
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  let label = t('order_within')
  if (hours > 0) {
    label += `${hours} ${hours === 1 ? t('hour') : t('hours')}`
    if (minutes > 0) {
      label += ` ${minutes} ${minutes === 1 ? t('min') : t('mins')}`
    }
  } else if (minutes > 0) {
    label += `${minutes} ${minutes === 1 ? t('min') : t('mins')}`
  } else {
    label += t('order_within_1_minute')
  }

  return label.trim()
}

export interface EddLabelDateParts {
  before: string
  date: string
  after: string
}

/**
 * Split an EDD sentence so the weekday+date segment can be bolded. Supports US (Mon, Jan 15) and UK (Mon, 15 Jan).
 */
export const parseDateFromEddLabel = (label: string): EddLabelDateParts => {
  const supportedDatePatterns = [
    /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s\d{1,2}\b/,
    /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s\d{1,2}\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/,
  ]
  const match = supportedDatePatterns.map(datePattern => label.match(datePattern)).find(Boolean)
  const date = match?.[0] ?? label
  const parts = label.split(date)
  return { before: parts[0] ?? '', date, after: parts[1] ?? '' }
}
